import { Children, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Menu } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import IconPlus from 'src/assets/icons/Plus';
import { IconUpload } from 'src/assets/icons/Upload';
import { CUSTOM_BUTTON_VARIANT, CustomButton } from 'src/components/CustomButton';
import { DividerWithText } from 'src/components/DividerWithText';
import { PROMPT_MENU_TYPE } from 'src/components/RichSlate';
import { SearchField } from 'src/components/SearchField';
import { UploadResourceFileHelper } from 'src/components/UploadResourceFileHelper';
import { MAX_RESOURCE_PER_TYPE } from 'src/data/common';
import { RESOURCE_CONTENT_TYPE } from 'src/data/performance';
import * as events from 'src/lib/events';
import { useFilteredResources, useKeyHandlers, useResourcesUniqueTags } from 'src/lib/hooks';
import { isValidUrl } from 'src/lib/string';

import { ACTION } from '../../const';
import { getMenuCards } from '../../utils';
import { EmptyMenuState } from '../EmptyMenuState';
import { ExternalChannelMenu } from '../ExternalChannelMenu';
import { Filters } from '../Filters';
import { MenuCard } from '../MenuCard';
import { PromptMenuHeader } from '../PromptMenuHeader';
import { SearchHint } from '../SearchHint';
import { SearchResults } from '../SearchResults';

import { useHandlers } from './useHandlers';
import { useMenus } from './useMenus';
import { useStyles } from './useStyles';

export const PromptMenu = forwardRef(
  (
    {
      menuType,
      setMenuType,
      menuVisible,
      menuAnchor,
      menuProps,
      onCloseMenu,
      onKeyDownMenu,
      brandBriefsMenuItems,
      noTags,
      urlResources,
      imageResources,
      documentResources,
      isUploadingImage,
      isApplicationModeInfinity,
      hideImages,
      isUploadingDocument,
      index,
      shouldOpenConfirmPopup,
      searchValue,
      setSearchValue,
      quickSearchMode,
      externalChannelMenuProps,
      onAction,
      displayAlternativeTitle,
      shouldIncludeMp4,
    },
    ref
  ) => {
    const styles = useStyles();
    const uploadFileHelperRef = useRef(null);

    const [filteredBankItems, setFilteredBankItems] = useState(brandBriefsMenuItems);
    const [resourcesTypeFilter, setResourcesTypeFilter] = useState([]);
    const [resourcesTagFilter, setResourcesTagFilter] = useState([]);

    useKeyHandlers({ Escape: onCloseMenu });

    useEffect(() => {
      setSearchValue('');
      setResourcesTypeFilter([]);
      setResourcesTagFilter([]);
    }, [menuType, setSearchValue]);

    const resources = useMemo(
      () => [...urlResources, ...imageResources, ...documentResources],
      [urlResources, imageResources, documentResources]
    );

    const uniqueTags = useResourcesUniqueTags({ resources });

    const categorizedResources = useFilteredResources({
      resources,
      searchText: searchValue,
      typeFilter: resourcesTypeFilter,
      tagFilter: resourcesTagFilter,
      shouldBeCategorized: true,
    });

    const filteredUrlResources = categorizedResources[RESOURCE_CONTENT_TYPE.url];
    const filteredDocumentResources = categorizedResources[RESOURCE_CONTENT_TYPE.document];
    const filteredImageResources = categorizedResources[RESOURCE_CONTENT_TYPE.image];

    useEffect(() => setFilteredBankItems(brandBriefsMenuItems), [brandBriefsMenuItems]);

    const { messagingBankMenu, documentMenu, imagesMenu, urlsMenu, documentItems, imageItems } =
      useMenus({
        menuType,
        searchText: searchValue,
        filteredBankItems,
        filteredUrlResources,
        filteredDocumentResources,
        filteredImageResources,
        noTags,
        isApplicationModeInfinity,
        quickSearchMode,
        onAction,
      });

    const menuCards = getMenuCards({
      hideImages,
      shouldIncludeExternalChannel: !!externalChannelMenuProps,
      checkExternalChannelConnected: externalChannelMenuProps?.onCheckExternalChannelConnected,
      shouldIncludeMp4,
    });

    const {
      handleUploadFile,
      handleSearchChange,
      handleMenuCardClick,
      handleChangeResourcesTagFilter,
      handleChangeResourcesTypeFilter,
    } = useHandlers({
      imageItems,
      documentItems,
      setMenuType,
      onCloseMenu,
      isUploadingImage,
      isUploadingDocument,
      index,
      setSearchValue,
      brandBriefsMenuItems,
      setFilteredBankItems,
      resourcesTypeFilter,
      resourcesTagFilter,
      setResourcesTagFilter,
      setResourcesTypeFilter,
      searchValue,
      onConnectExternalChannel: externalChannelMenuProps?.onConnectExternalChannel,
      onAction,
      shouldOpenConfirmPopup,
      uploadFileHelperRef,
    });

    return (
      <Menu
        open={menuVisible && !(menuType === PROMPT_MENU_TYPE.image && imageItems?.length === 0)}
        ref={ref}
        anchorEl={menuAnchor}
        variant="menu"
        classes={{
          paper: styles.menuPaper,
          list: styles.menuList,
        }}
        onClose={() => {
          onCloseMenu();
          // menu is closed with animation, so we need to delay this a bit
          setTimeout(() => {
            setMenuType(PROMPT_MENU_TYPE.default);
          }, 250);
        }}
        onKeyDown={(event) => {
          if (menuType !== PROMPT_MENU_TYPE.all) {
            onKeyDownMenu(event);
          }
        }}
        onKeyDownCapture={(event) => {
          if (menuType !== PROMPT_MENU_TYPE.all) {
            event.stopPropagation();
          }
        }}
        getContentAnchorEl={null}
        {...menuProps}
      >
        {!quickSearchMode && (
          <PromptMenuHeader
            menuType={menuType}
            onChangeMenuType={setMenuType}
            onCloseMenu={onCloseMenu}
            displayAlternativeTitle={displayAlternativeTitle}
          />
        )}

        <div
          className={styles.buttonsWrapper}
          data-testid="promptInstructionsMenuContent"
        >
          {menuType === PROMPT_MENU_TYPE.default && (
            <div className={styles.menuCardsWrapper}>
              <div className={styles.menuCards}>
                {menuCards.map((menuCard) => (
                  <MenuCard
                    key={menuCard.type}
                    menuCard={menuCard}
                    onClick={handleMenuCardClick}
                  />
                ))}
              </div>
              <DividerWithText text="OR" />
              <SearchField
                placeholder="Search by name or tag"
                className={styles.searchField}
                onFocus={() => {
                  onAction(ACTION.trackEvent, {
                    event: events.PROMPTS.promptResourceSearchDisplayed,
                    trigger: 'button_click',
                  });
                  setMenuType(PROMPT_MENU_TYPE.all);
                }}
                onChange={(value) => handleSearchChange(value, PROMPT_MENU_TYPE.all)}
                value={searchValue}
                autoFocus={menuType === PROMPT_MENU_TYPE.all}
              />
            </div>
          )}

          {menuType === PROMPT_MENU_TYPE.all && (
            <>
              <SearchField
                placeholder="Search by name or tag"
                className={styles.searchField}
                onFocus={() => setMenuType(PROMPT_MENU_TYPE.all)}
                onChange={(value) => {
                  handleSearchChange(value, PROMPT_MENU_TYPE.all);
                }}
                value={searchValue}
                autoFocus={menuType === PROMPT_MENU_TYPE.all}
              />
              {(!quickSearchMode || (quickSearchMode && searchValue)) && (
                <Filters
                  resourcesTypeFilter={resourcesTypeFilter}
                  onChangeResourcesTypeFilter={handleChangeResourcesTypeFilter}
                  resourcesTagFilter={resourcesTagFilter}
                  onChangeResourcesTagFilter={handleChangeResourcesTagFilter}
                  tags={uniqueTags}
                />
              )}
              {(!searchValue &&
                !resourcesTypeFilter.length &&
                !resourcesTagFilter.length &&
                !quickSearchMode) ||
              (quickSearchMode && !searchValue) ? (
                <SearchHint quickSearchMode={quickSearchMode} />
              ) : (
                <SearchResults
                  resourcesTypeFilter={resourcesTypeFilter}
                  onChangeResourcesTypeFilter={setResourcesTypeFilter}
                  resourcesTagFilter={resourcesTagFilter}
                  onChangeResourcesTagFilter={setResourcesTagFilter}
                  tags={uniqueTags}
                  urlResources={filteredUrlResources}
                  documentResources={filteredDocumentResources}
                  imageResources={filteredImageResources}
                  bankItems={filteredBankItems}
                  urlsMenu={urlsMenu}
                  documentMenu={documentMenu}
                  messagingBankMenu={messagingBankMenu}
                  imagesMenu={imagesMenu}
                  searchValue={searchValue}
                />
              )}
            </>
          )}

          {menuType === PROMPT_MENU_TYPE.image && (
            <div>
              <CustomButton
                className={styles.buttonHeader}
                onClick={() => handleUploadFile(RESOURCE_CONTENT_TYPE.image)}
                startIcon={<IconUpload />}
                variant={CUSTOM_BUTTON_VARIANT.outlined}
              >
                Upload
              </CustomButton>

              <div className={styles.menuListImage}>{imagesMenu}</div>
            </div>
          )}

          {menuType === PROMPT_MENU_TYPE.bank && (
            <div>
              <CustomButton
                className={styles.buttonHeader}
                onClick={() => onAction(ACTION.addBrandBrief)}
                startIcon={<IconPlus />}
                variant={CUSTOM_BUTTON_VARIANT.outlined}
                data-testid="addNewMessagingBankButton"
              >
                Add new
              </CustomButton>

              {messagingBankMenu}
            </div>
          )}

          {menuType === PROMPT_MENU_TYPE.url && (
            <>
              <div>
                <SearchField
                  className={cx(styles.searchField, styles.extraMarginBottom)}
                  onChange={(value) => handleSearchChange(value, menuType)}
                  onEnterPress={() => {
                    if (shouldOpenConfirmPopup([RESOURCE_CONTENT_TYPE.url])) {
                      onAction(ACTION.displayMessage, {
                        message: `You can use a maximum of ${MAX_RESOURCE_PER_TYPE} ${RESOURCE_CONTENT_TYPE.url.toUpperCase()}s in a prompt.`,
                      });
                      return;
                    }
                    let prefix = '';
                    // check if value starts with http, if not, add https for the validUrl check
                    if (!searchValue.startsWith('http')) {
                      prefix = 'https://';
                    }
                    if (isValidUrl(prefix + searchValue)) {
                      onAction(ACTION.insertUrl, { url: searchValue, noTags });
                    }
                  }}
                  value={searchValue}
                  placeholder="Enter URL"
                  autoFocus
                  hideIcon
                />
              </div>

              {Children.count(urlsMenu) > 0 ? (
                urlsMenu
              ) : (
                <EmptyMenuState
                  searchText={searchValue}
                  emptyText="No saved resources yet"
                />
              )}
            </>
          )}

          {menuType === PROMPT_MENU_TYPE.document && (
            <div>
              <CustomButton
                className={styles.buttonHeader}
                onClick={() => handleUploadFile(RESOURCE_CONTENT_TYPE.document)}
                startIcon={<IconUpload />}
                variant={CUSTOM_BUTTON_VARIANT.outlined}
              >
                Upload
              </CustomButton>

              {documentMenu}
            </div>
          )}

          {[PROMPT_MENU_TYPE.salesforce, PROMPT_MENU_TYPE.microsoftDynamicsSales].includes(
            menuType
          ) && (
            <ExternalChannelMenu
              onAction={onAction}
              menuType={menuType}
              {...externalChannelMenuProps}
            />
          )}
        </div>

        <UploadResourceFileHelper ref={uploadFileHelperRef} />
      </Menu>
    );
  }
);

PromptMenu.propTypes = {
  menuType: PropTypes.string,
  setMenuType: PropTypes.func.isRequired,
  menuVisible: PropTypes.bool,
  menuAnchor: PropTypes.shape(),
  menuProps: PropTypes.shape(),
  onCloseMenu: PropTypes.func.isRequired,
  onKeyDownMenu: PropTypes.func.isRequired,
  brandBriefsMenuItems: PropTypes.arrayOf(PropTypes.shape()),
  noTags: PropTypes.bool,
  urlResources: PropTypes.arrayOf(PropTypes.shape()),
  imageResources: PropTypes.arrayOf(PropTypes.shape()),
  isUploadingImage: PropTypes.bool,
  isApplicationModeInfinity: PropTypes.bool,
  hideImages: PropTypes.bool,
  documentResources: PropTypes.arrayOf(PropTypes.shape({})),
  isUploadingDocument: PropTypes.bool,
  index: PropTypes.number,
  shouldOpenConfirmPopup: PropTypes.func.isRequired,
  searchValue: PropTypes.string,
  setSearchValue: PropTypes.func.isRequired,
  quickSearchMode: PropTypes.bool,
  externalChannelMenuProps: PropTypes.shape({
    onConnectExternalChannel: PropTypes.func.isRequired,
    onCheckExternalChannelConnected: PropTypes.func.isRequired,
  }),
  onAction: PropTypes.func.isRequired,
  displayAlternativeTitle: PropTypes.bool,
  shouldIncludeMp4: PropTypes.bool,
};

PromptMenu.defaultProps = {
  menuType: '',
  menuVisible: false,
  menuAnchor: null,
  menuProps: {},
  brandBriefsMenuItems: [],
  noTags: false,
  urlResources: [],
  imageResources: [],
  documentResources: [],
  isUploadingImage: false,
  isApplicationModeInfinity: false,
  hideImages: false,
  isUploadingDocument: false,
  index: 0,
  searchValue: '',
  quickSearchMode: false,
  externalChannelMenuProps: null,
  displayAlternativeTitle: false,
  shouldIncludeMp4: false,
};
